import { Helmet } from "react-helmet-async";

const SITE_URL = "https://fostercareawareness365.com";
const DEFAULT_OG_IMAGE = "https://customer-assets.emergentagent.com/job_redemption-songs/artifacts/u1pk93uo_FosterCareAnthem_VOL1_Cover.png";

/**
 * Per-route SEO component using react-helmet-async.
 * Works both at runtime (CSR) and at build-time (react-snap prerender).
 *
 * Props:
 * - title:        required
 * - description:  required
 * - path:         path-only canonical (e.g. "/about"); defaults to "/"
 * - image:        absolute OG image URL (defaults to album cover)
 * - type:         OG type (website | article); defaults website
 */
/**
 * Build a BreadcrumbList JSON-LD for any non-root route.
 * e.g. "/anthem/catalog" → Home > Anthem > Catalog
 */
function buildBreadcrumb(path) {
  if (!path || path === "/") return null;
  const segments = path.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": `${SITE_URL}/`,
    },
  ];
  let acc = "";
  segments.forEach((seg, i) => {
    acc += `/${seg}`;
    // Pretty-print the segment (kebab-case → Title Case)
    const name = seg
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    items.push({
      "@type": "ListItem",
      "position": i + 2,
      "name": name,
      "item": `${SITE_URL}${acc}`,
    });
  });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items,
  };
}

export default function SEO({ title = "Foster Care Awareness 365", description, path = "/", image = DEFAULT_OG_IMAGE, type = "website", jsonLd = null }) {
  const url = `${SITE_URL}${path}`;
  const fullTitle = title.includes("Foster Care Awareness 365") ? title : `${title} | Foster Care Awareness 365`;
  const breadcrumb = buildBreadcrumb(path);
  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Foster Care Awareness 365" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* BreadcrumbList JSON-LD — auto-generated for every non-root route */}
      {breadcrumb && (
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
      )}

      {/* Optional per-route JSON-LD structured data (FAQPage, ProfilePage, etc.) */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
